// Optional: Möbelstück im Foto antippen und markieren als "kommt nicht mit", "entsorgen" oder (seit 16.10.2026
// wieder) "ab- und aufbauen" – die Firma sieht es als Kundenwunsch und bepreist es selbst. Überspringbar.
// Seit 30.10.2026 kein Schritt im Ablauf mehr, sondern eine Unterseite von „Besonderes“ (Link dort, zurück dorthin).

import { useRef, useState } from "react";
import { CUSTOMER_MARK_ACTIONS, MARK_ACTION_LABEL, type MarkAction } from "@umzugsscan/shared";
import { useDraft, useStore } from "../hooks.ts";
import { useNav } from "../Erfassung.tsx";
import { Vorschau, usePhotoUrl } from "../ui/Bild.tsx";
import { Button, Footer } from "../ui/elemente.tsx";
import type { Mark, Photo } from "../zustand.ts";

export function Markieren() {
  const d = useDraft();
  const { go } = useNav();
  const zurueck = () => go({ screen: "besonderes" });
  const [open, setOpen] = useState<string | null>(null);
  const photo = d.photos.find((p) => p.id === open);
  const total = d.photos.reduce((n, p) => n + p.marks.length, 0);

  return (
    <>
      <h1>Möbel markieren</h1>
      <p className="lead">
        Öffnen Sie ein Foto und tippen Sie auf ein Möbelstück, um es zu markieren: „kommt nicht mit“,
        „entsorgen“ oder „ab- und aufbauen“ (z. B. Schrank oder Bett, das wir zerlegen und wieder aufbauen sollen).
      </p>
      {d.rooms.map((r) => {
        const photos = d.photos.filter((p) => p.roomId === r.id);
        if (photos.length === 0) return null;
        return (
          <section key={r.id}>
            <h2>{r.name}</h2>
            <div className="grid">
              {photos.map((p) => (
                <Vorschau key={p.id} photo={p} onClick={() => setOpen(p.id)} />
              ))}
            </div>
          </section>
        );
      })}
      <Footer onBack={zurueck} onNext={zurueck} nextLabel={total > 0 ? `Fertig (${total} markiert)` : "Fertig"} />
      {photo && <Editor photo={photo} onClose={() => setOpen(null)} />}
    </>
  );
}

const COLOR: Record<MarkAction, string> = { bleibt: "mk-bleibt", entsorgen: "mk-entsorgen", abbau_aufbau: "mk-abbau" };

function Editor(props: { photo: Photo; onClose: () => void }) {
  const store = useStore();
  const url = usePhotoUrl(props.photo);
  const box = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState<{ x: number; y: number } | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [label, setLabel] = useState("");
  const marks = props.photo.marks;

  const tap = (e: React.MouseEvent) => {
    const r = box.current!.getBoundingClientRect();
    setSelected(null);
    setLabel("");
    setPending({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
  };
  const add = (action: MarkAction) => {
    if (!pending) return;
    const m: Mark = { x: round(pending.x), y: round(pending.y), action, label: label.trim() || null };
    store.setMarks(props.photo.id, [...marks, m]);
    setPending(null);
  };
  const remove = (i: number) => {
    store.setMarks(props.photo.id, marks.filter((_, j) => j !== i));
    setSelected(null);
  };

  return (
    <div className="editor" role="dialog" aria-modal="true" aria-label="Möbel markieren">
      <div className="editor-top">
        <span>Auf ein Möbelstück tippen</span>
        <Button wide={false} variant="secondary" onClick={props.onClose}>
          Fertig
        </Button>
      </div>
      <div className="editor-img">
        {/* Rahmen genau so groß wie das Bild: Tippen und Markierungen beziehen sich aufs Foto, nicht auf den Rand. */}
        <div className="editor-frame" ref={box} onClick={tap}>
        {url && <img src={url} alt="Foto zum Markieren" draggable={false} />}
        {marks.map((m, i) => (
          <button
            key={i}
            type="button"
            className={`marker ${COLOR[m.action]}${selected === i ? " sel" : ""}`}
            style={{ left: `${m.x * 100}%`, top: `${m.y * 100}%` }}
            aria-label={`${MARK_ACTION_LABEL[m.action]}${m.label ? `: ${m.label}` : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setPending(null);
              setSelected(i);
            }}
          >
            {i + 1}
          </button>
        ))}
        {pending && <span className="marker marker-new" style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }} />}
        </div>
      </div>
      <div className="sheet">
        {pending ? (
          <>
            <input
              className="sheet-input"
              placeholder="Was ist das? (optional, z. B. Sofa)"
              value={label}
              maxLength={80}
              onChange={(e) => setLabel(e.target.value)}
            />
            {CUSTOMER_MARK_ACTIONS.map((a) => (
              <button key={a} type="button" className={`btn btn-wide ${COLOR[a]}`} onClick={() => add(a)}>
                {MARK_ACTION_LABEL[a]}
              </button>
            ))}
            <Button variant="quiet" onClick={() => setPending(null)}>
              Abbrechen
            </Button>
          </>
        ) : selected !== null && marks[selected] ? (
          <>
            <p>
              <strong>{selected + 1}.</strong> {MARK_ACTION_LABEL[marks[selected].action]}
              {marks[selected].label && ` – ${marks[selected].label}`}
            </p>
            <Button variant="danger" onClick={() => remove(selected)}>
              Markierung entfernen
            </Button>
          </>
        ) : (
          <ul className="legend">
            {marks.length === 0 && <li>Noch nichts markiert.</li>}
            {marks.map((m, i) => (
              <li key={i}>
                <span className={`dot ${COLOR[m.action]}`}>{i + 1}</span> {MARK_ACTION_LABEL[m.action]}
                {m.label && ` – ${m.label}`}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const round = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 1000) / 1000;
